import { first } from "lodash";
import { useCallback } from "react";
import { useDispatch } from "react-redux";

import { useShallowEqualSelector } from "../../../common/hooks/useShallowEqualSelector";
import {
  type CreateIntermodalBookingFormModel,
  getCreateIntermodalBookingFormState,
  getCreateIntermodalBookingIsSubmitting,
  getCreateIntermodalBookingValidationErrors,
  setCreateFormValue,
} from "../../../services/intermodal/createIntermodalReducer";
import type { StoreState } from "../../../store/storeState";
import {
  type TrackerAction,
  transformCreateIntermodalFieldToTrackerAction,
} from "../../simple-create/hooks/UseTrackingTextInput";

export interface UseCreateIntermodalFieldProps<
  Key extends keyof CreateIntermodalBookingFormModel,
> {
  disabled: boolean;
  editedValue: CreateIntermodalBookingFormModel[Key] | undefined;
  journeyValidationError?: string;
  onChange: (value: CreateIntermodalBookingFormModel[Key]) => void;
  trackerAction: TrackerAction;
  validationError?: string;
}

export const useCreateIntermodalField = <
  Key extends keyof CreateIntermodalBookingFormModel,
>(
  name: Key,
): UseCreateIntermodalFieldProps<Key> => {
  const mapState = useCallback(
    (state: StoreState) => {
      const formState = getCreateIntermodalBookingFormState(state);
      const validationErrors = getCreateIntermodalBookingValidationErrors(
        state,
        name,
      );
      const disabled = getCreateIntermodalBookingIsSubmitting(state);
      return {
        value: formState[name],
        validationError: first(validationErrors),
        disabled,
      };
    },
    [name],
  );

  const { value, validationError, disabled } =
    useShallowEqualSelector(mapState);

  const trackerAction = transformCreateIntermodalFieldToTrackerAction(name);

  const dispatch = useDispatch();
  const onChange = useCallback<
    (value: CreateIntermodalBookingFormModel[Key]) => void
  >(
    (newValue) => {
      if (value !== newValue) {
        dispatch(setCreateFormValue({ [name]: newValue }));
      }
    },
    [dispatch, name, value],
  );

  return {
    editedValue: value,
    onChange,
    validationError,
    disabled,
    trackerAction,
  };
};
